/**
 * Component: RestaurantDashboard
 * Author: Dine AI
 * Date: July 16, 2025
 * Last Modified: July 16, 2025
 * 
 * Purpose:
 * - Main dashboard view for restaurant owners
 * - Displays menu status, AI impact metrics, and available features
 * - Handles location switching for multi-location restaurants
 * - Shows subscription-specific features and usage limits
 * - Central navigation hub for all restaurant management features
 * 
 * Props: None (uses authentication context)
 * 
 * State Management:
 * - Restaurant data from API
 * - Active menu and location information
 * - AI image usage statistics
 * - Subscription features and limits
 * 
 * Performance:
 * - Lazy loads feature cards based on subscription
 * - Caches restaurant data in component state
 * - Optimized re-renders with proper dependency arrays
 * 
 * Error Handling:
 * - Graceful fallbacks for missing data
 * - Redirects to login on auth failure
 * - Shows appropriate messages for subscription states
 */
import { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import AICoach from '@/components/onboarding/AICoach';
import {
  Menu,
  Eye,
  Edit,
  TrendingUp,
  Settings,
  LogOut,
  User,
  Users,
  CreditCard,
  Bell,
  ChevronDown,
  Sparkles,
  ArrowRight,
  Apple,
  Play,
  Image,
  MapPin,
  Zap,
  Brain,
  Crown,
  Headphones,
  Rocket,
  Star,
  MoreVertical,
  RefreshCw,
  Table2
} from 'lucide-react';
import Logo from '@/components/Logo';
import SettingsDropdown from '@/components/dashboard/SettingsDropdown';
import DashboardHeader from '@/components/dashboard/DashboardHeader';
import { usePageTitle } from '@/hooks/usePageTitle';
import { getUserDisplayInfo, getUserSubscriptionStatus } from '@/utils/jwt';
import { restaurantApi } from '@/services/api/restaurantApi';
import { apiClient } from '@/services/api/apiClient';
import AIImageGenerationCard from '@/components/dashboard/AIImageGenerationCard';
import AddLocationCard from '@/components/dashboard/AddLocationCard';
import DineAIImpactCardHorizontal from '@/components/dashboard/DineAIImpactCardHorizontal';
import ResubscribePrompt from '@/components/ResubscribePrompt';
import SeasonalMenuCard from '@/components/dashboard/SeasonalMenuCard';
import HeroCarouselCard from '@/components/dashboard/HeroCarouselCard';
import CustomerInsightsCard from '@/components/dashboard/CustomerInsightsCard';
import AIImageAddonCard from '@/components/dashboard/AIImageAddonCard';
import { toast } from 'sonner';
import Cookies from 'js-cookie';

interface RestaurantDashboardProps {
  sidebarExpanded: boolean;
}

const RestaurantDashboard: React.FC<RestaurantDashboardProps> = ({ sidebarExpanded }) => {
  const navigate = useNavigate();
  const [restaurantData, setRestaurantData] = useState<any>(null);
  const [activeMenu, setActiveMenu] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [imageUsage, setImageUsage] = useState<{used: number; limit: number; remaining: number} | null>(null);
  const [menuStats, setMenuStats] = useState<{
    totalItems: number;
    itemsWithImages: number;
    itemsWithoutImages: number;
    aiGeneratedImages: number;
    userUploadedImages: number;
  } | null>(null);
  const [showResubscribePrompt, setShowResubscribePrompt] = useState(false);
  const [dashboardMetrics, setDashboardMetrics] = useState<{
    dine_ai_recommendations: number;
    dine_ai_orders: number;
  } | null>(null);

  // Set SEO-optimized title for Dashboard
  usePageTitle('dashboard');

  // Click outside handler for dropdowns
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      // Close location dropdown - check both button and dropdown menu
      if (
        locationButtonRef.current &&
        !locationButtonRef.current.contains(event.target as Node) &&
        locationDropdownRef.current &&
        !locationDropdownRef.current.contains(event.target as Node)
      ) {
        setIsLocationDropdownOpen(false);
      }

      // Close edit menu dropdown - check both button and dropdown menu
      if (
        editMenuButtonRef.current &&
        !editMenuButtonRef.current.contains(event.target as Node) &&
        editMenuDropdownRef.current &&
        !editMenuDropdownRef.current.contains(event.target as Node)
      ) {
        setIsEditMenuDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // AI Coach state - DISABLED/DEFERRED FOR NOW
  const [showAICoach, setShowAICoach] = useState(false);
  
  // Reset modal state on mount to prevent stale state issues
  useEffect(() => {
    // AI COACH DISABLED - Deferred to future release
    // Uncomment below when ready to enable AI Coach onboarding
    /*
    const hasSeenCoach = localStorage.getItem('ai-coach-onboarding-complete');
    if (!hasSeenCoach) {
      setTimeout(() => {
        setShowAICoach(true);
      }, 1500);
    } else {
      setShowAICoach(true);
    }
    */
  }, []);
  const [hasMultipleLocations, setHasMultipleLocations] = useState(false);
  const [currentLocation, setCurrentLocation] = useState<any>(null);
  const [locations, setLocations] = useState<any[]>([]);
  const [isLocationDropdownOpen, setIsLocationDropdownOpen] = useState(false);
  const locationButtonRef = useRef<HTMLButtonElement>(null);
  const locationDropdownRef = useRef<HTMLDivElement>(null);

  // Edit Menu dropdown state
  const [isEditMenuDropdownOpen, setIsEditMenuDropdownOpen] = useState(false);
  const editMenuButtonRef = useRef<HTMLButtonElement>(null);
  const editMenuDropdownRef = useRef<HTMLDivElement>(null);

  // Cache rebuild modal state
  const [showCacheRebuildModal, setShowCacheRebuildModal] = useState(false);
  const [isRebuildingCache, setIsRebuildingCache] = useState(false);

  // Check if this is a demo user by looking for demo session
  const isDemoUser = localStorage.getItem('isDemo') === 'true';
  
  // Get user info from auth token
  const authToken = Cookies.get('auth_token');
  let userInfo = null;
  
  // For demo users, we'll get user info from the restaurant data instead of JWT
  if (!isDemoUser && authToken) {
    try {
      userInfo = getUserDisplayInfo(authToken);
    } catch (e) {
    }
  }
  
  const restaurantName = restaurantData?.restaurant_name || "Loading...";
  
  // Subscription data from API
  const [subscriptionData, setSubscriptionData] = useState<{
    plan: string;
    isTrial: boolean;
    status: string;
  } | null>(null);
  const [upgradeOptions, setUpgradeOptions] = useState<any>(null);
  
  // Features from API
  const [subscriptionFeatures, setSubscriptionFeatures] = useState<{
    tier: string;
    hasMenuAssistant: boolean;
    hasPOSIntegration: boolean;
    hasAnalytics: boolean;
    hasAIImages: boolean;
    hasAdvancedInsights: boolean;
    hasCustomerInsights: boolean;
    aiImagesPerYear?: number;
  }>({
    tier: 'no_plan',
    hasMenuAssistant: false,
    hasPOSIntegration: false,
    hasAnalytics: false,
    hasAIImages: false,
    hasAdvancedInsights: false,
    hasCustomerInsights: false,
    aiImagesPerYear: 0
  });
  
  // Use owner name from restaurant profile if available, fallback to JWT token
  const ownerFirstName = restaurantData?.owner_first_name || userInfo?.firstName || userInfo?.fullName?.split(' ')[0] || 'Restaurant';
  const ownerLastName = restaurantData?.owner_last_name || userInfo?.lastName || '';
  const ownerName = restaurantData?.owner_full_name || userInfo?.fullName || 'Restaurant Owner';

  // Fetch image usage data
  const fetchImageUsage = async () => {
    try {
      const response = await apiClient.get('/images/usage');
      setImageUsage(response.data);
    } catch (error) {
      console.error('[RestaurantDashboard] Failed to fetch image usage:', error);
      // Use API-provided limit if available
      const limit = subscriptionFeatures.aiImagesPerYear || 0;
      setImageUsage({ used: 0, limit, remaining: limit });
    }
  };

  // Calculate menu stats from active menu
  const calculateMenuStats = async (menuId: number, regularItemsCount?: number, totalItemsCount?: number) => {
    // First set the count we know from the profile
    const knownItemCount = regularItemsCount || totalItemsCount || 0;
    
    // Set initial stats with known values
    setMenuStats({
      totalItems: knownItemCount,
      regularItemsCount: knownItemCount,
      itemsWithImages: 0,
      itemsWithoutImages: knownItemCount,
      aiGeneratedImages: 0,
      userUploadedImages: 0
    });
    
    try {
      // Try to fetch detailed menu items to calculate image stats
      const response = await apiClient.get(`/menus/${menuId}/items`);
      const items = response.data.items || [];
      
      const stats = {
        totalItems: regularItemsCount !== undefined ? regularItemsCount : items.length,
        regularItemsCount: regularItemsCount !== undefined ? regularItemsCount : items.length,
        itemsWithImages: 0,
        itemsWithoutImages: 0,
        aiGeneratedImages: 0,
        userUploadedImages: 0
      };
      
      items.forEach((item: any) => {
        // Check for AI generated image or uploaded image based on the response structure
        if (item.ai_image_url) {
          stats.itemsWithImages++;
          stats.aiGeneratedImages++;
        } else if (item.uploaded_image_url) {
          stats.itemsWithImages++;
          stats.userUploadedImages++;
        } else {
          stats.itemsWithoutImages++;
        }
      });
      
      setMenuStats(stats);
    } catch (error: any) {
      console.error('[RestaurantDashboard] Failed to fetch detailed menu stats:', error);
      console.error('[RestaurantDashboard] Using basic counts from profile - menuId:', menuId, 'regularItems:', regularItemsCount, 'totalItems:', totalItemsCount);
      // Keep the initial stats we set above - don't override with 0
    }
  };

  // Fetch restaurant profile on mount
  useEffect(() => {
    const fetchRestaurantProfile = async () => {
      try {
        const isDemo = localStorage.getItem('isDemo') === 'true';

        // Priority 1: Check URL params (highest priority)
        const urlParams = new URLSearchParams(window.location.search);
        const urlRestId = urlParams.get('rest_id');

        // Priority 2: For demo users, let backend use session data (don't pass stale localStorage)
        // Priority 3: For non-demo users, use undefined (backend will use JWT data)
        let restIdToUse: number | undefined = urlRestId ? parseInt(urlRestId) : undefined;

        // If we have URL param, use it and clear stale localStorage
        if (urlRestId) {
          const storedRestId = localStorage.getItem('current_rest_id');
          if (storedRestId && storedRestId !== urlRestId) {
            console.warn(`[Dashboard] localStorage rest_id (${storedRestId}) doesn't match URL (${urlRestId}), clearing stale data`);
            localStorage.removeItem('current_rest_id');
          }
        }

        const profile = await restaurantApi.getProfile(restIdToUse);

        setRestaurantData(profile);

        // Store rest_id from backend response (source of truth)
        if (profile.rest_id) {
          const newRestId = profile.rest_id.toString();
          const oldRestId = localStorage.getItem('current_rest_id');

          if (oldRestId && oldRestId !== newRestId) {
            console.warn(`[Dashboard] Updating stale rest_id in localStorage: ${oldRestId} → ${newRestId}`);
          }

          localStorage.setItem('current_rest_id', newRestId);
        }
        
        // Set subscription data from profile
        // Use VA subscription for VA dashboard (with fallback to subscription_plan for backward compatibility)
        const vaPlan = profile.va_subscription?.plan || profile.subscription_plan;
        if (vaPlan) {
          const subData = {
            plan: vaPlan,
            isTrial: profile.va_subscription?.is_trial ?? profile.is_trial ?? false,
            status: profile.va_subscription?.status || profile.subscription_status || 'active'
          };
          setSubscriptionData(subData);
          
          // Check if subscription is cancelled and user needs to resubscribe
          if (profile.subscription_status === 'cancelled' && !isDemoUser) {
            setShowResubscribePrompt(true);
          }
        } else if (isDemoUser) {
          // For demo users, set default subscription data if not provided
          const subData = {
            plan: 'delicious',
            isTrial: false,
            status: 'active'
          };
          setSubscriptionData(subData);
        } else if (!isDemoUser) {
          // No subscription found - show resubscribe prompt
          setShowResubscribePrompt(true);
        }
        
        // Set features from API
        if (profile.subscription_features) {
          setSubscriptionFeatures({
            tier: profile.va_subscription?.plan || profile.subscription_plan || 'no_plan',
            hasMenuAssistant: profile.subscription_features.hasMenuAssistant || false,
            hasPOSIntegration: profile.subscription_features.hasPOSIntegration || false,
            hasAnalytics: profile.subscription_features.hasAnalytics || false,
            hasAIImages: profile.subscription_features.hasAIImages || false,
            hasAdvancedInsights: profile.subscription_features.hasAdvancedInsights || false,
            hasCustomerInsights: profile.subscription_features.hasCustomerInsights || false,
            aiImagesPerYear: profile.subscription_features.aiImagesPerYear || 0
          });
        }
        
        // Set active menu from profile if it exists
        if (profile.active_menu) {
          setActiveMenu(profile.active_menu);
          // Calculate menu stats when we have an active menu
          await calculateMenuStats(
            profile.active_menu.menu_id, 
            profile.active_menu.regular_items_count,
            profile.active_menu.total_items
          );
        }
        
        // Store seasonal menu info if it exists
        if (profile.seasonal_menu) {
          // We'll use this to show the "Regular Menu" label
          setRestaurantData({ ...profile, has_seasonal_menu: true });
        }
        
      } catch (error) {
        console.error('Failed to fetch restaurant profile:', error);
      } finally {
        setLoading(false);
      }
    };
    
    fetchRestaurantProfile();
    
    // Fetch upgrade options
    const fetchUpgradeOptions = async () => {
      try {
        const response = await apiClient.get('/subscription/upgrade-options');
        setUpgradeOptions(response.data);
      } catch (error) {
        console.error('Failed to fetch upgrade options:', error);
      }
    };
    
    fetchUpgradeOptions();
  }, []);
  
  // Check locations when restaurant data is loaded
  useEffect(() => {
    if (restaurantData) {
      checkMultipleLocations(restaurantData);
    }
  }, [restaurantData]);
  
  // Check if user has multiple locations and get current location
  const checkMultipleLocations = async (profileData?: any) => {
    try {
      const response = await apiClient.get('/locations');
      // location_routes returns an object with locations array
      const locationsList = response.data.locations || [];
      setLocations(locationsList);
      const hasMultiple = locationsList.length > 1;
      setHasMultipleLocations(hasMultiple);
      
      // Use provided profile data or fall back to state
      const currentProfile = profileData || restaurantData;
      
      // Find current location from auth context (rest_id from profile)
      if (currentProfile?.rest_id && locationsList.length > 0) {
        const current = locationsList.find((loc: any) => loc.restaurant_id === currentProfile.rest_id);
        setCurrentLocation(current || locationsList[0]);
      }
    } catch (error) {
      console.error('Failed to check locations:', error);
    }
  };

  // Fetch dashboard metrics (Dine AI Recommendations and Orders)
  useEffect(() => {
    const fetchDashboardMetrics = async () => {
      try {
        const response = await apiClient.get('/analytics/dashboard-metrics');
        setDashboardMetrics(response.data);
      } catch (error) {
        console.error('[RestaurantDashboard] Failed to fetch dashboard metrics:', error);
        // Set defaults on error
        setDashboardMetrics({
          dine_ai_recommendations: 0,
          dine_ai_orders: 0
        });
      }
    };

    fetchDashboardMetrics();
  }, []);

  // Fetch image usage when component mounts or subscription features change
  useEffect(() => {
    if (subscriptionFeatures.hasAIImages) {
      fetchImageUsage();
    }
  }, [subscriptionFeatures, subscriptionData?.plan]); // Also refetch when plan changes

  // Handle AI Image Add-on purchase success/cancel
  useEffect(() => {
    const hash = window.location.hash;
    if (hash === '#ai_purchase_success') {
      // Show success message
      
      // Show success toast
      toast.success('Purchase successful! 100 AI images have been added to your account.', {
        description: 'Your new credits are available immediately.',
        duration: 5000,
      });
      
      // Refresh image usage to show new credits
      // Always fetch after a successful purchase, regardless of plan
      fetchImageUsage();
      
      // Clean the URL
      window.history.replaceState(null, '', window.location.pathname);
    } else if (hash === '#ai_purchase_cancelled') {
      
      // Show info toast
      toast.info('Purchase cancelled', {
        description: 'No charges were made to your account.',
        duration: 3000,
      });
      
      // Clean the URL
      window.history.replaceState(null, '', window.location.pathname);
    }
  }, [subscriptionFeatures]);

  const handleLocationSwitch = async (location: any) => {

    setIsLocationDropdownOpen(false);

    // Don't do anything if clicking on the current location
    if (restaurantData?.rest_id === location.restaurant_id) {
      return;
    }

    const isDemo = localStorage.getItem('isDemo') === 'true';

    try {
      if (isDemo) {
        // For demo users, we can't switch locations via the database
        // Demo users have a fixed rest_id in their session
        // Just update the UI to show the selected location
        setCurrentLocation(location);
        localStorage.setItem('current_rest_id', location.restaurant_id.toString());

        // Fetch menu data for the selected location
        // This will show the menu items for that location
        window.location.reload();
      } else {
        // Step 1: Switch location in database for regular users
        const switchResponse = await apiClient.post('/locations/switch', {
          rest_id: location.restaurant_id
        });

        if (!switchResponse.data.success) {
          throw new Error('Failed to switch location');
        }

        // Location switched successfully in database
        // Update local storage with the new rest_id
        localStorage.setItem('current_rest_id', location.restaurant_id.toString());

        // Reload page to refresh all data with new location context
        // The JWT already contains all owned restaurant IDs, so no token refresh needed
        window.location.reload();
      }
    } catch (error) {
      console.error('Failed to switch location:', error);
      toast.error('Failed to switch location. Please try again.');
    }
  };

  const handleEditMenu = () => {
    // Always navigate directly - location is already selected in header
    if (activeMenu?.menu_id) {
      const restId = currentLocation?.restaurant_id || restaurantData?.rest_id || localStorage.getItem('current_rest_id');
      navigate(`/restaurant-menu-review?processing_id=${activeMenu.menu_id}&rest_id=${restId}`);
    } else {
      navigate('/restaurant-menu-upload');
    }
  };

  const handlePreviewMenu = () => {
    // Always navigate directly - location is already selected in header
    if (activeMenu?.menu_id) {
      // Don't include rest_id in URL for preview (public endpoint)
      // The component will get it from auth context
      navigate(`/menu-preview?menu_id=${activeMenu.menu_id}`);
    } else {
      navigate('/restaurant-menu-upload');
    }
  };

  const handleGenerateAIImages = () => {
    // Always navigate directly - location is already selected in header
    if (activeMenu?.menu_id) {
      // Include rest_id for consistency
      const restId = currentLocation?.restaurant_id || restaurantData?.rest_id || localStorage.getItem('current_rest_id');
      navigate(`/restaurant-menu-review?processing_id=${activeMenu.menu_id}&aiMode=true&rest_id=${restId}`);
    } else {
      navigate('/restaurant-menu-upload');
    }
  };

  const handleRebuildCache = async () => {
    if (!activeMenu?.menu_id) {
      toast.error('No active menu found');
      return;
    }

    setIsRebuildingCache(true);
    setShowCacheRebuildModal(false);

    try {
      const response = await apiClient.post(`/menus/${activeMenu.menu_id}/rebuild-cache`);

      if (response.data.success) {
        toast.success('Menu refreshed successfully!', {
          description: `${response.data.items_cached} menu items updated.`,
          duration: 5000,
        });
      } else {
        toast.error('Failed to refresh menu', {
          description: response.data.message || 'Unknown error occurred',
          duration: 5000,
        });
      }
    } catch (error: any) {
      console.error('Failed to rebuild cache:', error);
      toast.error('Failed to refresh menu', {
        description: error.response?.data?.detail || error.message || 'Please try again later',
        duration: 5000,
      });
    } finally {
      setIsRebuildingCache(false);
      setIsEditMenuDropdownOpen(false);
    }
  };


  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-gray-600 text-xl">Loading restaurant data...</div>
      </div>
    );
  }

  // Show resubscribe prompt if subscription is cancelled
  if (showResubscribePrompt && !isDemoUser) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col">
        {/* Header with Logo */}
        <div className="container mx-auto px-4 py-6">
          <Logo className="h-8" />
        </div>

        {/* Resubscribe Prompt */}
        <div className="flex-1 flex items-center justify-center px-4">
          <ResubscribePrompt
            onSuccess={() => {
              setShowResubscribePrompt(false);
              window.location.reload();
            }}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col bg-gray-50">
      {/* Fixed Header - Using shared DashboardHeader */}
      <DashboardHeader
        restaurantName={restaurantName}
        hasMultipleLocations={hasMultipleLocations}
        currentLocation={currentLocation}
        locations={locations}
        restaurantData={restaurantData}
        userEmail={restaurantData?.email || userInfo?.email || 'User'}
        userName={`${ownerFirstName} ${ownerLastName}`.trim() || ''}
        isDemoUser={isDemoUser}
        onLocationSwitch={handleLocationSwitch}
        sidebarExpanded={sidebarExpanded}
        restaurantLogoUrl={restaurantData?.restaurant_logo_url}
        welcomeName={ownerFirstName}
        isStaff={false}
      />

      {/* Scrollable Content Area */}
      <div className="flex-1 overflow-y-auto relative z-10 pt-24">
        {/* Content Area */}
        <div className="max-w-7xl mx-auto px-6">
          {/* Page Title */}
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="mt-10 mb-8"
          >
            <div className="flex items-center gap-4">
              <h2 className="text-2xl font-bold text-gray-900">Restaurant Overview</h2>
              {/* Plan Badge */}
              {subscriptionData?.plan && (
                <div className="flex items-center gap-2">
                  <span className={`
                    px-3 py-1 rounded-full text-sm font-semibold inline-flex items-center gap-1
                    ${subscriptionData.plan === 'gourmet'
                      ? 'bg-purple-100 text-purple-700 border border-purple-200'
                      : subscriptionData.plan === 'delicious'
                      ? 'bg-blue-100 text-blue-700 border border-blue-200'
                      : 'bg-yellow-100 text-yellow-700 border border-yellow-200'
                    }
                  `}>
                    {subscriptionData.plan === 'gourmet' && <Crown className="w-4 h-4" />}
                    {subscriptionData.plan.charAt(0).toUpperCase() + subscriptionData.plan.slice(1)} Plan
                  </span>
                  {subscriptionData.isTrial && subscriptionData.plan?.toLowerCase() === 'delicious' && (
                    <span className="px-2 py-1 rounded-full text-xs font-semibold bg-green-100 text-green-700 border border-green-200">
                      30-Day Free Trial
                    </span>
                  )}
                </div>
              )}
            </div>
          </motion.div>

          {/* Metrics Cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mb-10">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="bg-white rounded-2xl border border-gray-200 shadow-sm p-6"
            >
              <p className="text-4xl font-bold text-gray-900 mb-2">{menuStats?.regularItemsCount || menuStats?.totalItems || 0}</p>
              <p className="text-gray-500">Regular Menu Items</p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="bg-white rounded-2xl border border-gray-200 shadow-sm p-6"
            >
              <p className="text-4xl font-bold text-green-600 mb-2">
                {menuStats && menuStats.totalItems > 0
                  ? Math.round((menuStats.itemsWithImages / menuStats.totalItems) * 100) + '%'
                  : '0%'
                }
              </p>
              <p className="text-gray-500">With Images</p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
              className="bg-white rounded-2xl border border-gray-200 shadow-sm p-6 relative"
            >
              <span className="absolute top-3 right-3 text-gray-400 text-xs">Last 7 Days</span>
              <p className="text-4xl font-bold text-blue-600 mb-2">
                {dashboardMetrics?.dine_ai_recommendations ?? 0}
              </p>
              <p className="text-gray-500">Dine AI Recommendations</p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5 }}
              className="bg-white rounded-2xl border border-gray-200 shadow-sm p-6 relative"
            >
              <span className="absolute top-3 right-3 text-gray-400 text-xs">Last 7 Days</span>
              <p className="text-4xl font-bold text-blue-600 mb-2">
                {dashboardMetrics?.dine_ai_orders ?? 0}
              </p>
              <p className="text-gray-500">Dine AI Orders</p>
            </motion.div>
          </div>

          {/* Primary Actions Section */}
          <div className="mb-10">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 items-stretch">
              {/* Edit Menu - Primary Action Card */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
                onClick={handleEditMenu}
                className="relative bg-white rounded-xl border border-gray-200 shadow-sm p-6 hover:shadow-md hover:border-gray-300 transition-all cursor-pointer flex flex-col min-h-[310px] md:h-[310px] group"
              >
                {/* 3-dot menu button */}
                <button
                  ref={editMenuButtonRef}
                  onClick={(e) => {
                    e.stopPropagation(); // Prevent card click
                    setIsEditMenuDropdownOpen(!isEditMenuDropdownOpen);
                  }}
                  className="absolute top-4 right-4 p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                  disabled={isRebuildingCache}
                >
                  <MoreVertical className="w-5 h-5" />
                </button>

                {/* Dropdown menu */}
                {isEditMenuDropdownOpen && (
                  <div
                    ref={editMenuDropdownRef}
                    className="absolute top-14 right-4 w-56 bg-white border border-gray-200 rounded-lg shadow-xl z-50 overflow-hidden"
                  >
                    <div className="py-1">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setIsEditMenuDropdownOpen(false);
                          const restId = currentLocation?.restaurant_id || restaurantData?.rest_id || localStorage.getItem('current_rest_id');
                          navigate(`/menu-table-editor?processing_id=${activeMenu?.menu_id}&rest_id=${restId}`);
                        }}
                        className="w-full px-4 py-3 text-left hover:bg-gray-50 transition-colors flex items-center gap-3 border-b border-gray-100"
                      >
                        <Table2 className="w-4 h-4 text-blue-600" />
                        <div>
                          <div className="text-black text-sm font-medium">Edit in Table</div>
                          <div className="text-gray-500 text-xs">Spreadsheet-style bulk editing</div>
                        </div>
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setIsEditMenuDropdownOpen(false);
                          setShowCacheRebuildModal(true);
                        }}
                        className="w-full px-4 py-3 text-left hover:bg-gray-50 transition-colors flex items-center gap-3"
                      >
                        <RefreshCw className="w-4 h-4 text-green-600" />
                        <div>
                          <div className="text-black text-sm font-medium">Refresh Menu</div>
                          <div className="text-gray-500 text-xs">Update menu data</div>
                        </div>
                      </button>
                    </div>
                  </div>
                )}

                <div className="mb-6">
                  <div className="bg-gray-100 group-hover:bg-blue-100 p-4 rounded-xl w-fit transition-colors">
                    <Edit className="h-8 w-8 text-gray-400 group-hover:text-blue-600 transition-colors" />
                  </div>
                </div>
                <h4 className="text-gray-900 font-bold text-xl mb-3">
                  Edit Menu {restaurantData?.has_seasonal_menu && <span className="text-sm font-normal text-gray-500">(Regular Menu)</span>}
                </h4>
                <p className="text-gray-700 mb-2">{menuStats?.regularItemsCount || menuStats?.totalItems || 0} items ready to manage</p>
                <p className="text-gray-500 text-sm mb-6 flex-grow">Edit items, pricing, add new dishes, and upload images</p>
                <div className="flex items-center text-blue-600 font-medium mt-auto">
                  <span>Make changes</span>
                  <ArrowRight className="w-5 h-5 ml-auto" />
                </div>
              </motion.div>

              {/* Preview Menu Card */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
                onClick={handlePreviewMenu}
                className="bg-white rounded-xl border border-gray-200 shadow-sm p-6 hover:shadow-md hover:border-gray-300 transition-all cursor-pointer flex flex-col min-h-[310px] md:h-[310px] group"
              >
                <div className="mb-6">
                  <div className="bg-gray-100 group-hover:bg-green-100 p-4 rounded-xl w-fit transition-colors">
                    <Eye className="h-8 w-8 text-gray-400 group-hover:text-green-600 transition-colors" />
                  </div>
                </div>
                <h4 className="text-gray-900 font-bold text-xl mb-3">Preview Live Menu</h4>
                <p className="text-gray-700 mb-2">Live customer view</p>
                <p className="text-gray-500 text-sm mb-6 flex-grow">See how customers view your menu</p>
                <div className="flex items-center text-green-600 font-medium mt-auto">
                  <span>View menu</span>
                  <ArrowRight className="w-5 h-5 ml-auto" />
                </div>
              </motion.div>

              {/* AI Image Generation Card */}
              <div className="min-h-[310px] md:h-[310px] [&>div]:h-full [&>div]:shadow-sm hover:[&>div]:shadow-md [&>div]:transition-all">
                <AIImageGenerationCard 
                  subscriptionPlan={subscriptionData?.plan ? 
                    subscriptionData.plan.charAt(0).toUpperCase() + subscriptionData.plan.slice(1) : 
                    'Yummy'}
                  menuId={activeMenu?.menu_id}
                  usage={imageUsage || undefined}
                  menuStats={menuStats || undefined}
                  onGenerateClick={handleGenerateAIImages}
                />
              </div>
            </div>

          </div>

          {/* Main Content Grid */}
          <div className="grid lg:grid-cols-3 gap-6 mb-10">
            {/* Left Column - Analytics and Insights */}
            <div className="lg:col-span-2 space-y-6">


            {/* Dine AI Impact Dashboard - All Plans */}
            <DineAIImpactCardHorizontal 
              plan={subscriptionData?.plan || 'yummy'} 
            />

            {/* Customer Insights Card - Delicious & Gourmet Only (No upgrade prompt for Yummy) */}
            {subscriptionFeatures.hasCustomerInsights && (
              <CustomerInsightsCard 
                plan={subscriptionData?.plan || 'yummy'}
                hasAccess={true}
              />
            )}

            {/* AI Image Add-on Card - Available for ALL Plans */}
            <AIImageAddonCard 
              plan={subscriptionData?.plan || 'yummy'}
              imageUsage={imageUsage}
              onPurchaseComplete={fetchImageUsage}
            />

            </div>

            {/* Right Column - Seasonal Menu and Mobile Analytics */}
            <div className="space-y-6">
              {/* Seasonal Menu Card - Same height as primary cards */}
              <div className="h-[310px] overflow-hidden">
                <SeasonalMenuCard />
              </div>

              {/* Hero Carousel Card - Menu Assistant featured items */}
              <div className="h-[310px] overflow-hidden">
                <HeroCarouselCard />
              </div>

              {/* Add Location Card - For Delicious and Gourmet Plans */}
              {(subscriptionData?.plan?.toLowerCase() === 'delicious' || 
                subscriptionData?.plan?.toLowerCase() === 'gourmet') && (
                <AddLocationCard />
              )}

              {/* Dynamic Upgrade Card - Use backend options if available, fallback to current plan */}
              {(() => {
              
              // Check if user is on Gourmet plan - if so, don't show any upgrade card
              const currentPlan = subscriptionData?.plan?.toLowerCase();
              if (currentPlan === 'gourmet') {
                return null;
              }
              
              // Try to use backend upgrade options first
              if (upgradeOptions?.upgrade_options?.length > 0) {
                const nextPlan = upgradeOptions.upgrade_options[0];
                const planName = nextPlan.plan_name.toLowerCase();
                
                if (planName === 'delicious') {
                  /* Upgrade to Delicious - Light Mode */
                return (
                  <motion.div
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.2 }}
                    className="bg-white rounded-xl p-6 border border-gray-200 shadow-sm"
                  >
                    <div className="flex items-center gap-3 mb-4">
                      <div className="p-2 bg-blue-100 rounded-lg">
                        <TrendingUp className="h-6 w-6 text-blue-600" />
                      </div>
                      <h3 className="text-xl font-semibold text-gray-900">Upgrade to {nextPlan.display_name}</h3>
                    </div>

                    <p className="mb-6 text-gray-500">
                      Unlock advanced analytics, multi-location support, and customer preference insights.
                      Track revenue performance and optimize your business in real-time.
                    </p>

                    <button
                      onClick={() => navigate(`/plan-selection?mode=upgrade&targetPlan=${nextPlan.plan_name}`)}
                      className="w-full bg-[#FDB913] hover:bg-[#e5a711] text-black rounded-lg py-3 px-4 font-medium transition-all"
                    >
                      Upgrade to {nextPlan.display_name} Plan
                    </button>

                    <div className="mt-6 pt-6 border-t border-gray-200">
                      <p className="text-sm text-gray-500 mb-3">What you'll get:</p>
                      <ul className="space-y-2 text-sm text-gray-600">
                        <li className="flex items-center gap-2">
                          <TrendingUp className="h-4 w-4 text-blue-600" />
                          Analytics Dashboard
                        </li>
                        <li className="flex items-center gap-2">
                          <Users className="h-4 w-4 text-purple-600" />
                          Customer Preference Insights
                        </li>
                        <li className="flex items-center gap-2">
                          <MapPin className="h-4 w-4 text-green-600" />
                          Up to 2 Locations
                        </li>
                        <li className="flex items-center gap-2">
                          <Sparkles className="h-4 w-4 text-yellow-600" />
                          150 AI Images per year (vs 50)
                        </li>
                        <li className="flex items-center gap-2">
                          <Headphones className="h-4 w-4 text-blue-600" />
                          Priority Support
                        </li>
                      </ul>
                    </div>
                  </motion.div>
                );
              } else if (planName === 'gourmet') {
                /* Upgrade to Gourmet - Light Mode */
                return (
                  <motion.div
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.2 }}
                    className="bg-white rounded-xl p-6 border border-gray-200 shadow-sm"
                  >
                    <div className="flex items-center gap-3 mb-4">
                      <div className="p-2 bg-purple-100 rounded-lg">
                        <Crown className="h-6 w-6 text-purple-600" />
                      </div>
                      <h3 className="text-xl font-semibold text-gray-900">Upgrade to {nextPlan.display_name}</h3>
                    </div>

                    <p className="mb-6 text-gray-500">
                      Best for restaurants with 5+ locations. Custom pricing with AI image allocation per location,
                      POS integration, and everything in Delicious.
                    </p>

                    <button
                      onClick={() => navigate('/contact')}
                      className="w-full bg-[#FDB913] hover:bg-[#e5a711] text-black rounded-lg py-3 px-4 font-medium transition-all"
                    >
                      Contact Sales
                    </button>

                    <div className="mt-6 pt-6 border-t border-gray-200">
                      <p className="text-sm text-gray-500 mb-3">What you'll get:</p>
                      <ul className="space-y-2 text-sm text-gray-600">
                        <li className="flex items-center gap-2">
                          <Sparkles className="h-4 w-4 text-yellow-600" />
                          AI Image Allocation per Location
                        </li>
                        <li className="flex items-center gap-2">
                          <MapPin className="h-4 w-4 text-green-600" />
                          Minimum 5 Restaurants
                        </li>
                        <li className="flex items-center gap-2">
                          <Star className="h-4 w-4 text-blue-600" />
                          Everything in Delicious
                        </li>
                        <li className="flex items-center gap-2">
                          <Rocket className="h-4 w-4 text-purple-600" />
                          POS Integration on Request
                        </li>
                      </ul>
                    </div>
                  </motion.div>
                );
                } else {
                  /* Default card when no upgrades available from backend */
                  return null;
                }
              } else {
                // Fallback to original logic based on current plan
                let currentPlan = 'yummy'; // Default
                if (subscriptionData && subscriptionData.plan) {
                  currentPlan = String(subscriptionData.plan).toLowerCase();
                }
                
                if (currentPlan === 'yummy') {
                  /* Fallback: Upgrade to Delicious for Yummy Users - Light Mode */
                  return (
                    <motion.div
                      initial={{ opacity: 0, x: 20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: 0.2 }}
                      className="bg-white rounded-xl p-6 border border-gray-200 shadow-sm"
                    >
                      <div className="flex items-center gap-3 mb-4">
                        <div className="p-2 bg-blue-100 rounded-lg">
                          <TrendingUp className="h-6 w-6 text-blue-600" />
                        </div>
                        <h3 className="text-xl font-semibold text-gray-900">Upgrade to Delicious</h3>
                      </div>

                      <p className="mb-6 text-gray-500">
                        Unlock advanced analytics, multi-location support, and customer preference insights.
                        Track revenue performance and optimize your business in real-time.
                      </p>

                      <button
                        onClick={() => navigate('/plan-selection?mode=upgrade&targetPlan=delicious')}
                        className="w-full bg-[#FDB913] hover:bg-[#e5a711] text-black rounded-lg py-3 px-4 font-medium transition-all"
                      >
                        Upgrade to Delicious Plan
                      </button>

                      <div className="mt-6 pt-6 border-t border-gray-200">
                        <p className="text-sm text-gray-500 mb-3">What you'll get:</p>
                        <ul className="space-y-2 text-sm text-gray-600">
                          <li className="flex items-center gap-2">
                            <TrendingUp className="h-4 w-4 text-blue-600" />
                            Analytics Dashboard
                          </li>
                          <li className="flex items-center gap-2">
                            <Users className="h-4 w-4 text-purple-600" />
                            Customer Preference Insights
                          </li>
                          <li className="flex items-center gap-2">
                            <MapPin className="h-4 w-4 text-green-600" />
                            Up to 2 Locations
                          </li>
                          <li className="flex items-center gap-2">
                            <Sparkles className="h-4 w-4 text-yellow-600" />
                            75 AI Images per year
                          </li>
                        </ul>
                      </div>
                    </motion.div>
                  );
                } else if (currentPlan === 'delicious' || currentPlan === 'essential') {
                  /* Fallback: Upgrade to Gourmet for Delicious Users - Light Mode */
                  return (
                    <motion.div
                      initial={{ opacity: 0, x: 20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: 0.2 }}
                      className="bg-white rounded-xl p-6 border border-gray-200 shadow-sm"
                    >
                      <div className="flex items-center gap-3 mb-4">
                        <div className="p-2 bg-purple-100 rounded-lg">
                          <Crown className="h-6 w-6 text-purple-600" />
                        </div>
                        <h3 className="text-xl font-semibold text-gray-900">Upgrade to Gourmet</h3>
                      </div>

                      <p className="mb-6 text-gray-500">
                        Best for restaurants with 5+ locations. Custom pricing with AI image allocation per location,
                        POS integration, and everything in Delicious.
                      </p>

                      <button
                        onClick={() => navigate('/contact')}
                        className="w-full bg-[#FDB913] hover:bg-[#e5a711] text-black rounded-lg py-3 px-4 font-medium transition-all"
                      >
                        Contact Sales
                      </button>

                      <div className="mt-6 pt-6 border-t border-gray-200">
                        <p className="text-sm text-gray-500 mb-3">What you'll get:</p>
                        <ul className="space-y-2 text-sm text-gray-600">
                          <li className="flex items-center gap-2">
                            <Sparkles className="h-4 w-4 text-yellow-600" />
                            AI Image Allocation per Location
                          </li>
                          <li className="flex items-center gap-2">
                            <MapPin className="h-4 w-4 text-green-600" />
                            Minimum 5 Restaurants
                          </li>
                          <li className="flex items-center gap-2">
                            <Star className="h-4 w-4 text-blue-600" />
                            Everything in Delicious
                          </li>
                          <li className="flex items-center gap-2">
                            <Rocket className="h-4 w-4 text-purple-600" />
                            POS Integration on Request
                          </li>
                        </ul>
                      </div>
                    </motion.div>
                  );
                }
                
                return null;
              }
            })()}

              {/* Support Card */}
              <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.3 }}
              className="bg-white rounded-xl p-6 border border-gray-200 shadow-sm"
            >
              <h3 className="text-lg font-semibold text-gray-900 mb-3">Need Help?</h3>
              <p className="text-gray-600 mb-4">Our support team is here to help you succeed.</p>
              <a
                href="mailto:support@dineai.ai"
                className="text-blue-600 hover:text-blue-700 font-medium transition-colors"
              >
                support@dineai.ai
              </a>
            </motion.div>
            </div>
          </div>

          {/* Removed duplicate footer - using the one at the bottom */}
        </div>
      </div>

      
      {/* Footer with Legal Links - Break out of sidebar margin to span full width */}
      <div className={`pt-6 pb-6 border-t border-gray-200 bg-white ${sidebarExpanded ? '-ml-72 pl-72' : '-ml-28 pl-28'}`} style={{ width: 'calc(100% + ' + (sidebarExpanded ? '18rem' : '7rem') + ')' }}>
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <div className="text-gray-500 text-sm">
              © {new Date().getFullYear()} Dine AI LLC. All rights reserved.
            </div>
            <div className="flex items-center gap-6 text-sm">
              <a
                href="/terms"
                target="_blank"
                className="text-gray-500 hover:text-blue-600 transition-colors"
              >
                Terms of Service
              </a>
              <a
                href="/privacy"
                target="_blank"
                className="text-gray-500 hover:text-blue-600 transition-colors"
              >
                Privacy Policy
              </a>
              <a
                href="/cookies"
                target="_blank"
                className="text-gray-500 hover:text-blue-600 transition-colors"
              >
                Cookie Policy
              </a>
              <a
                href="/accessibility"
                target="_blank"
                className="text-gray-500 hover:text-blue-600 transition-colors"
              >
                Accessibility
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* Refresh Menu Confirmation Modal */}
      {showCacheRebuildModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-[100] p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-xl p-8 max-w-md w-full shadow-2xl"
          >
            <div className="flex items-center gap-3 mb-4">
              <div className="p-3 bg-green-100 rounded-lg">
                <RefreshCw className="w-6 h-6 text-green-600" />
              </div>
              <h3 className="text-xl font-bold text-gray-900">Refresh Menu?</h3>
            </div>

            <p className="text-gray-600 mb-6">
              This will update your menu with the latest information. Your menu will stay live during this process.
            </p>

            <div className="flex gap-3">
              <button
                onClick={() => setShowCacheRebuildModal(false)}
                className="flex-1 px-4 py-3 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg font-medium transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleRebuildCache}
                disabled={isRebuildingCache}
                className="flex-1 px-4 py-3 bg-[#FDB913] hover:bg-[#e5a711] text-black rounded-lg font-medium transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 shadow-md"
              >
                {isRebuildingCache ? (
                  <>
                    <RefreshCw className="w-4 h-4 animate-spin" />
                    Refreshing...
                  </>
                ) : (
                  'Refresh Menu'
                )}
              </button>
            </div>
          </motion.div>
        </div>
      )}

      {/* AI Coach Onboarding Assistant - DEFERRED/DISABLED
          Keeping code for future use when user feedback indicates need for onboarding
      <AICoach
        isVisible={showAICoach}
        onDismiss={() => setShowAICoach(false)}
        context="dashboard"
      />
      */}
    </div>
  );
}

export default RestaurantDashboard;